import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import API from '../../API/Main/API';
import { FunderProperties, ClientFunderProperties, CreateClientFunderProperties } from '../../API/ClientAPIHelpers/funding';

interface FundingState {
	funders: FunderProperties[];
	clientFunders: ClientFunderProperties[];
	loading: boolean;
}

const initialState: FundingState = {
	funders: [],
	clientFunders: [],
	loading: false,
};

export const getFunders = createAsyncThunk('/funding/getFunders', async (_, thunkAPI) => {
	const response = await API.ClientAPI.Funding.getFunders();
	if (response.error) return thunkAPI.rejectWithValue(response.error);
	return response as FunderProperties[];
});

export const getClientFunders = createAsyncThunk(
	'/funding/getClientFunders',
	async (clientId: string, thunkAPI) => {
		const response = await API.ClientAPI.Funding.getClientFunders(clientId);
		if (response.error) return thunkAPI.rejectWithValue(response.error);
		return response as ClientFunderProperties[];
	},
);

export const addClientFunder = createAsyncThunk(
	'/funding/addClientFunder',
	async (data: CreateClientFunderProperties, thunkAPI) => {
		const response = await API.ClientAPI.Funding.addClientFunder(data);
		if (response.error) return thunkAPI.rejectWithValue(response.error);
		return response as ClientFunderProperties;
	},
);

export const removeClientFunder = createAsyncThunk(
	'/funding/removeClientFunder',
	async ({ clientId, clientFunderId }: { clientId: string; clientFunderId: string }, thunkAPI) => {
		const response = await API.ClientAPI.Funding.removeClientFunder(clientId, clientFunderId);
		if (response.error) return thunkAPI.rejectWithValue(response.error);
		return clientFunderId;
	},
);

const fundingSlice = createSlice({
	name: 'funding',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(getFunders.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getFunders.fulfilled, (state, action) => {
			state.loading = false;
			state.funders = action.payload;
		});
		builder.addCase(getFunders.rejected, (state) => {
			state.loading = false;
		});

		builder.addCase(getClientFunders.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getClientFunders.fulfilled, (state, action) => {
			state.loading = false;
			state.clientFunders = action.payload;
		});
		builder.addCase(getClientFunders.rejected, (state) => {
			state.loading = false;
		});

		builder.addCase(addClientFunder.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addClientFunder.fulfilled, (state, action) => {
			state.loading = false;
			state.clientFunders.push(action.payload);
		});
		builder.addCase(addClientFunder.rejected, (state) => {
			state.loading = false;
		});

		builder.addCase(removeClientFunder.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(removeClientFunder.fulfilled, (state, action) => {
			state.loading = false;
			state.clientFunders = state.clientFunders.filter((cf) => cf.id !== action.payload);
		});
		builder.addCase(removeClientFunder.rejected, (state) => {
			state.loading = false;
		});
	},
});

export default fundingSlice.reducer;
